import {
  Component,
  Input,
  OnInit,
  Output,
  EventEmitter,
  OnDestroy,
  SimpleChanges,
} from '@angular/core'
import Validator from '@shared/validatorjs/src/validator'
import { FormArray, FormBuilder, FormGroup } from '@angular/forms'
import { select, Store } from '@ngrx/store'
import { FetchPostprocessorSlotTypesRequest } from '@shared/shared.actions'
import { Subscription } from 'rxjs'
import { getPostprocessorSlotTypesState } from '@shared/shared.select'
import { getSettingsState } from '@containers/settings/settings.selectors'

@Component({
  selector: 'form-postprocessor-builder',
  templateUrl: './form-postprocessor-builder.component.html',
  styleUrls: ['./form-postprocessor-builder.component.scss'],
})
export class FormPostprocessorBuilderComponent implements OnInit, OnDestroy {
  public form: FormGroup
  @Input() isLoading: boolean
  @Input() errors: any
  @Input() data: any
  @Output() onSubmit = new EventEmitter<any>()
  @Output() onValidationFailed = new EventEmitter<any>()
  isPropertyTypesOpen = false
  validationErrors: Map<string, string> = new Map()
  slotTypes = []
  slotTypes$: Subscription
  propertyTypeRowIndex: number
  public settings: any = {}
  private settings$: Subscription

  constructor(private fb: FormBuilder, public store: Store<any>) {
    this.form = this.fb.group({
      name: [''],
      items: this.fb.array([]),
    })
  }

  ngAfterViewInit() {
    this.settings$ = this.store
      .pipe(select(getSettingsState))
      .subscribe((state) => {
        if (state.getIn(['data', 'units'])) {
          this.settings = state.getIn(['data', 'units']).toJS()
        }
      })
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes.data &&
      changes.data.currentValue &&
      JSON.stringify(this.form.value) !==
        JSON.stringify(changes.data.currentValue)
    ) {
      this.patchForm(changes.data.currentValue)
    }
  }

  handlePropertyTypeChange(type: any) {
    const item = this.items.at(this.propertyTypeRowIndex)
    item.get('propertyType').setValue(type)
  }

  get slotTypeKeys() {
    return this.slotTypes.map((slotType) => slotType.trnKey)
  }

  get slotTypeDefaults() {
    return this.slotTypes.reduce(
      (prev, { trnKey, defaultName }) => ({
        ...prev,
        [trnKey]: defaultName,
      }),
      {},
    )
  }

  ngOnDestroy() {
    if (this.slotTypes$) {
      this.slotTypes$.unsubscribe()
    }
  }

  ngOnInit() {
    this.store.dispatch<any>(new FetchPostprocessorSlotTypesRequest())
    this.slotTypes$ = this.store
      .pipe(select(getPostprocessorSlotTypesState))
      .subscribe((state) => {
        this.slotTypes = state?.toJS()
        if (this.data && this.slotTypes?.length) {
          this.patchForm(this.data)
        }
      })
  }

  validation() {
    const data = this.form.value
    const rules = {
      name: 'required',
      'items.*.propertyType': 'required',
      'items.*.slots.*.name': 'required',
      'items.*.slots.*.type': 'required',
      'items.*.slots.*.value': 'required|min:0',
      'items.*.slots.*.rangeStart': 'required|min:0',
      'items.*.slots.*.rangeEnd': 'required_unless_last_row|min:0',
    }

    Validator.register(
      'required_unless_last_row',
      function (value, requirement, attribute) {
        const match = attribute.match(/items\.\d+\.slots\.(\d+)\.rangeEnd/)
        if (match) {
          const slotIndex = parseInt(match[1], 10)
          const itemMatch = attribute.match(/items\.(\d+)\.slots/)
          const itemIndex = itemMatch ? parseInt(itemMatch[1], 10) : -1

          const slots = data.items[itemIndex]?.slots || []
          return slotIndex === slots.length - 1 || value !== null
        }

        return true
      },
      'This field is required unless it is the last row.',
    )

    const validator = new Validator(data, rules)

    return validator
  }

  get items(): FormArray {
    return this.form.get('items') as FormArray
  }

  addItem(): void {
    this.items.push(
      this.fb.group({
        id: [''],
        propertyType: [''],
        slots: this.fb.array([]),
      }),
    )
  }

  removeItem(index: number): void {
    this.items.removeAt(index)
  }

  addSlot(itemIndex: number): void {
    const slots = this.items.at(itemIndex).get('slots') as FormArray
    slots.push(
      this.fb.group({
        name: [''],
        type: [''],
        rangeStart: [null],
        rangeEnd: [null],
        value: [''],
      }),
    )
  }

  patchForm(data: any): void {
    this.form = this.fb.group({
      name: [''],
      items: this.fb.array([]),
    })
    this.form.patchValue({
      name: data.name || '',
    })
    if (data.items) {
      data.items.forEach((item: any) => {
        const itemGroup = this.fb.group({
          id: [item.id || ''],
          propertyType: [item.propertyType || ''],
          slots: this.fb.array([]),
        })
        if (item.slots) {
          item.slots.forEach((slot: any) => {
            ;(itemGroup.get('slots') as FormArray).push(
              this.fb.group({
                name: [slot.name || ''],
                type: [slot.type || ''],
                rangeStart: [
                  slot.rangeStart === undefined ? null : slot.rangeStart,
                ],
                rangeEnd: [slot.rangeEnd === undefined ? null : slot.rangeEnd],
                value: [slot.value === undefined ? '' : slot.value],
              }),
            )
          })
        }
        this.items.push(itemGroup)
      })
    }
  }

  removeSlot(itemIndex: number, slotIndex: number): void {
    const slots = this.items.at(itemIndex).get('slots') as FormArray
    slots.removeAt(slotIndex)
  }

  public handleSubmit(e: Event): void {
    e.preventDefault()
    const validation = this.validation()

    if (validation.passes()) {
      const formValue = {
        ...this.form.value,
        items: this.form.value.items.map((item: any) => ({
          ...item,
          slots: item.slots.map((slot: any) => ({
            ...slot,
            rangeStart: parseFloat(slot.rangeStart),
            rangeEnd: parseFloat(slot.rangeEnd),
            value: parseFloat(slot.value),
          })),
        })),
      }
      this.onSubmit.emit(formValue)
    } else {
      this.validationErrors = new Map(Object.entries(validation.errors.errors))
    }
  }
}
